// Utility functions for API calls

import axios from 'axios';
import { API_BASE_URL } from '../config/api';

/**
 * Make an authenticated API call using cookies
 * @param {string} url - The API endpoint URL
 * @param {string} method - The HTTP method (GET, POST, PUT, DELETE)
 * @param {object} data - The request body data (for POST, PUT)
 * @param {object} additionalHeaders - Additional headers to include
 * @returns {Promise} - The API response
 */
export const authenticatedFetch = async (url, method = 'GET', data = null, additionalHeaders = {}) => {
  try {
    // For backward compatibility, include token in Authorization header if available
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...additionalHeaders
    };
    
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    // Determine the full URL
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    
    // Make the request with credentials to include cookies
    const config = {
      method,
      url: fullUrl,
      headers,
      withCredentials: true // Include cookies in the request
    };
    
    // Add data to request if provided
    if (data) {
      config.data = data;
    }
    
    const response = await axios(config);
    return response.data;
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

/**
 * Make a GET request with authentication
 * @param {string} url - The API endpoint URL
 * @param {object} additionalHeaders - Additional headers to include
 * @returns {Promise} - The API response
 */
export const get = (url, additionalHeaders = {}) => {
  return authenticatedFetch(url, 'GET', null, additionalHeaders);
};

/**
 * Make a POST request with authentication
 * @param {string} url - The API endpoint URL
 * @param {object} data - The request body data
 * @param {object} additionalHeaders - Additional headers to include
 * @returns {Promise} - The API response
 */
export const post = (url, data, additionalHeaders = {}) => {
  return authenticatedFetch(url, 'POST', data, additionalHeaders);
};

/**
 * Make a PUT request with authentication
 * @param {string} url - The API endpoint URL
 * @param {object} data - The request body data
 * @param {object} additionalHeaders - Additional headers to include
 * @returns {Promise} - The API response
 */
export const put = (url, data, additionalHeaders = {}) => {
  return authenticatedFetch(url, 'PUT', data, additionalHeaders);
};

/**
 * Make a DELETE request with authentication
 * @param {string} url - The API endpoint URL
 * @param {object} additionalHeaders - Additional headers to include
 * @returns {Promise} - The API response
 */
export const del = (url, additionalHeaders = {}) => {
  return authenticatedFetch(url, 'DELETE', null, additionalHeaders);
};

/**
 * Check if the user is authenticated
 * @returns {boolean} - True if the user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('username');
};

/**
 * Get the current username
 * @returns {string} - The current username or empty string
 */
export const getUsername = () => {
  return localStorage.getItem('username') || '';
};

export default {
  get,
  post,
  put,
  del,
  isAuthenticated,
  getUsername
};
